import type { GetWorkspaceMembersQuery } from 'hooks/globalApi';
import clsx from 'clsx';
import { useToast } from 'components/ToastManager';
import {
  useCreateUserWorkspaceInviteMutation,
  useGetWorkspaceMembersQuery,
  WorkspaceRoleKey,
} from 'hooks/globalApi';
import {
  IndividualOnboardingRole,
  useInviteIndividualOnboardingApplicationMutation,
} from 'hooks/internalApi';
import { useGlobalApiContext } from 'hooks/useGlobalApiContext';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import { useWorkspaceId } from 'hooks/useWorkspace';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { match } from 'ts-pattern';
import { getResultOrError, hasUserFacingMessage } from 'utils/errors';
import { useCatchMutationError } from 'utils/urql';

import lilId from '@fragment/lil-id';
import { EmailAddressSchema } from '@fragment/types/strings';
import { Tooltip } from '@fragment/ui/src/components';
import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { SecondaryButtonV2 } from '@fragment/ui/src/components/Button/SecondaryButtonV2/SecondaryButtonV2';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { Toast } from '@fragment/ui/src/components/Toast/Toast';

const ADD_NEW_VALUE = 'add-new';

const validateEmail = (email: string) =>
  EmailAddressSchema.safeParse(email.toLowerCase()).success;

type WorkspaceMembers = Extract<
  GetWorkspaceMembersQuery['workspace'],
  { __typename: 'Workspace' }
>['members'];
type WorkspaceMemberNode = NonNullable<WorkspaceMembers['nodes'][number]>;

type PersonType = 'leader' | 'owner' | 'both';

const TypeButton = ({
  type,
  onClick,
  selected,
}: {
  type: PersonType;
  onClick: () => void;
  selected: boolean;
}) => (
  <Tooltip
    height="h-full"
    baseComponent={
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          'text-center h-full px-[1ch] hover:text-main',
          selected ? 'bg-main-200 ' : 'bg-canvas text-main-500'
        )}
      >
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </button>
    }
    tooltipComponent={
      <span className="text-main">
        {match(type)
          .with(
            'leader',
            () => 'A person in a significant leadership role at the company.'
          )
          .with(
            'owner',
            () => 'A person who owns more than a 25% stake in the company.'
          )
          .with('both', () => 'This person is both a leader and an owner.')
          .exhaustive()}
      </span>
    }
  />
);

type InviteNewMemberModalProps = {
  onClose: () => void;
  members: WorkspaceMemberNode[];
};

const InviteNewMemberModal = ({
  onClose,
  members,
}: InviteNewMemberModalProps) => {
  const workspaceId = useWorkspaceId();
  const { context } = useGlobalApiContext();
  const { showToast } = useToast();

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const showWarningsWhileTyping = useRef(false);

  const [{ fetching: isInviting }, createInvite] = useCatchMutationError(
    useCreateUserWorkspaceInviteMutation,
    'createUserWorkspaceInvite'
  );

  const validateInviteEmail = useCallback(
    (email: string, showWarning = true) => {
      const lower = email.toLowerCase();
      if (!validateEmail(lower)) {
        if (showWarning) setInviteError('Invalid email address');
        return false;
      }
      const alreadyMember = members.some(
        (m) => m.user.email?.toLowerCase() === lower
      );
      if (alreadyMember) {
        if (showWarning) setInviteError('User is already a workspace member');
        return false;
      }
      setInviteError(null);
      return true;
    },
    [members]
  );

  const sendInvite = useCallback(async () => {
    showWarningsWhileTyping.current = true;
    if (!validateInviteEmail(inviteEmail)) return;

    const result = await createInvite(
      {
        workspaceId,
        invites: [
          {
            email: inviteEmail.toLowerCase(),
            inviteId: lilId(),
            role: WorkspaceRoleKey.WorkspaceAdmin,
          },
        ],
      },
      context
    );

    if (result.error) {
      const message = hasUserFacingMessage(
        result.data?.createUserWorkspaceInvite
      )
        ? result.error.message
        : 'Failed to send invite. Please check the email and try again.';
      showToast(<Toast type="error" message={message} />);
    } else {
      showToast(
        <Toast type="success" message={`Invite sent to ${inviteEmail}`} />
      );
      onClose();
    }
  }, [
    createInvite,
    inviteEmail,
    validateInviteEmail,
    workspaceId,
    context,
    showToast,
    onClose,
  ]);

  return (
    <Modal
      width="sm"
      onSubmit={(e) => {
        e.preventDefault();
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- grandfathered
        sendInvite();
      }}
      onClose={onClose}
      header={<span className="text-main-500">Invite workspace member</span>}
      body={
        <div className="flex flex-col gap-y-f1">
          <div className="border-main-300 border-[0.5px] w-full">
            <TextInput
              autoFocus
              transparent
              width="w-full"
              align="left"
              flexContainer="space-y-f0"
              size="md"
              padding="p-f0"
              placeholder="Enter email address"
              value={inviteEmail}
              onChange={(e) => {
                setInviteEmail(e.target.value);
                if (showWarningsWhileTyping.current) {
                  validateInviteEmail(e.target.value, true);
                }
              }}
            />
          </div>
          {inviteError && (
            <div className="text-main flex items-center gap-f0">
              <Icon
                type="alert"
                backgroundColor="orange"
                primaryColor="white"
                size="sm"
              />
              {inviteError}
            </div>
          )}
        </div>
      }
      footer={
        <FormButtons
          type="tertiary"
          onCancel={onClose}
          cancelButtonLabel="Cancel"
          submitButtonLabel={isInviting ? 'Sending...' : 'Send Invite'}
          disabled={!inviteEmail.trim() || isInviting}
        />
      }
    />
  );
};

type PeopleViewProps = {
  onNext: () => void;
};

export const PeopleView = ({ onNext }: PeopleViewProps) => {
  const workspaceId = useWorkspaceId();
  const { context } = useGlobalApiContext();
  const { context: internalApiContext } = useInternalApiContext();
  const { showToast } = useToast();

  const [selectedEmail, setSelectedEmail] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);

  const [personType, setPersonType] = useState<PersonType>('leader');

  const [{ data, error }] = useGetWorkspaceMembersQuery({
    variables: { workspaceId },
    context,
  });

  const [{ fetching: isInviting }, inviteIndividual] = useCatchMutationError(
    useInviteIndividualOnboardingApplicationMutation,
    'inviteIndividualOnboardingApplication'
  );

  const { result: workspace } = getResultOrError(data?.workspace, error);
  const { result: members } = getResultOrError(workspace?.members, error);

  const memberNodes = useMemo<WorkspaceMemberNode[]>(
    () => members?.nodes.flatMap((m) => (m !== null ? [m] : [])) ?? [],
    [members]
  );

  const memberOptions = useMemo(
    () => [
      ...memberNodes.flatMap((member) => {
        if (!member.user.email) return [];
        return [{ value: member.user.email, label: member.user.email }];
      }),
      { value: ADD_NEW_VALUE, label: '+ Invite new workspace member' },
    ],
    [memberNodes]
  );

  const handleDropdownChange = (value: string) => {
    if (value === ADD_NEW_VALUE) {
      setShowInviteModal(true);
      setSelectedEmail('');
    } else {
      setSelectedEmail(value);
    }
  };

  const addPerson = useCallback(async () => {
    if (!selectedEmail) return;
    const member = memberNodes.find(
      (m) => m.user.email?.toLowerCase() === selectedEmail.toLowerCase()
    );
    if (!member) {
      showToast(
        <Toast
          type="error"
          message="Selected member not found. Please try again."
        />
      );
      return;
    }
    const role = match(personType)
      .with('leader', () => IndividualOnboardingRole.Controller)
      .with('owner', () => IndividualOnboardingRole.Owner)
      .with('both', () => IndividualOnboardingRole.Both)
      .exhaustive();
    const result = await inviteIndividual(
      {
        workspaceId,
        userId: member.user.id,
        email: selectedEmail,
        role,
      },
      internalApiContext
    );
    if (result.error) {
      showToast(<Toast type="error" message={result.error.message} />);
      return;
    }
    showToast(
      <Toast
        type="success"
        message={`Onboarding invite sent to ${selectedEmail}`}
      />
    );
    setSelectedEmail('');
  }, [
    selectedEmail,
    memberNodes,
    workspaceId,
    inviteIndividual,
    internalApiContext,
    showToast,
    personType,
  ]);

  return (
    <div className="flex flex-col h-fit gap-y-f2">
      <span className="text-main">
        As part of our compliance process, we will need to collect information
        about key individuals in the organization.
      </span>
      <span className="text-main">
        Invite someone in a significant leadership role at your company, such as
        the CEO, CFO, or other executive.
      </span>
      <span className="text-main">
        Invite every person who owns more than a 25% stake in the company.
      </span>
      <div className="flex flex-col gap-y-f1 border-[0.5px] border-main-300 p-f1">
        <span className="text-main-500">New Invite</span>
        <Dropdown
          displayValue={selectedEmail}
          onChange={handleDropdownChange}
          options={memberOptions}
          placeholder="Select a member"
          border
          width="w-full"
          height="h-f2"
          padding="p-f0"
          size="md"
          backgroundColor="bg-main-200"
        />
        <div className="flex flex-row justify-between gap-x-[1px] w-full">
          <div className="flex flex-row items-start border-[0.5px] border-main-300 h-f2">
            <TypeButton
              type="leader"
              onClick={() => setPersonType('leader')}
              selected={personType === 'leader'}
            />
            <TypeButton
              type="owner"
              onClick={() => setPersonType('owner')}
              selected={personType === 'owner'}
            />
            <TypeButton
              type="both"
              onClick={() => setPersonType('both')}
              selected={personType === 'both'}
            />
          </div>
          <SecondaryButtonV2
            // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
            onClick={addPerson}
            disabled={!selectedEmail || isInviting}
            className="w-fit"
          >
            {isInviting ? 'Sending...' : 'Invite'}
          </SecondaryButtonV2>
        </div>
      </div>
      <PrimaryButtonV2 onClick={onNext}>Next</PrimaryButtonV2>
      {showInviteModal && (
        <InviteNewMemberModal
          onClose={() => setShowInviteModal(false)}
          members={memberNodes}
        />
      )}
    </div>
  );
};
